import { useCallback, useEffect, useState } from 'react';

import { RunAsyncUntilCompleteTimedOutError } from '#/utils/functional/function/RunAsyncUntilCompleteTimedOutError';

import type {
  BackgroundTask,
  BackgroundTaskState,
} from './asyncBackendTaskTypes';
import { waitTaskCompleted } from './waitTaskCompleted';

const defaultTimeToResetMs = 3_000;

export type UseBackgroundTaskOptions = {
  timeToResetMs?: number;
  timeoutMs?: number;
};

export type RunBackgroundTaskFnResponse<TResult> = Promise<
  | { type: 'result'; result: TResult }
  | { type: 'timed-out'; error: RunAsyncUntilCompleteTimedOutError }
  | {
      type: 'request-failed';
      error: Error;
    }
>;

export type RunBackgroundTaskFn<TResult, TStep extends string> = (
  request: () => Promise<{ task: BackgroundTask<TResult, TStep> }>,
) => RunBackgroundTaskFnResponse<TResult>;

type Response<TResult, TStep extends string> = {
  backgroundTaskState: BackgroundTaskState<TResult, TStep> | undefined;
  runBackgroundTask: RunBackgroundTaskFn<TResult, TStep>;
};

export function useBackgroundTask<TResult, TStep extends string = ''>(
  options: UseBackgroundTaskOptions = {},
): Response<TResult, TStep> {
  const [state, setState] = useState<BackgroundTaskState<TResult, TStep>>();

  // Reset state back to undefined
  const resetState = useCallback(
    (multiplier: number) => {
      setTimeout(
        () => setState(undefined),
        (options.timeToResetMs ?? defaultTimeToResetMs) * multiplier,
      );
    },
    [options.timeToResetMs],
  );

  useEffect(() => {
    const type = state?.type;

    if (type === 'completed') resetState(1);
    else if (type === 'timed-out' || type === 'request-failed') resetState(2);
  }, [resetState, state?.type]);

  const onTickCompleted = useCallback(
    (task: BackgroundTask<TResult, TStep>) => {
      const { result } = task;
      setState({ type: 'completed', result });
    },
    [],
  );

  const onTick = useCallback(
    (task: BackgroundTask<TResult, TStep>) => {
      if (task.status === 'COMPLETED') onTickCompleted(task);
      else setState({ type: 'processing', progress: task.progress });
    },
    [onTickCompleted],
  );

  const runBackgroundTask: RunBackgroundTaskFn<TResult, TStep> = useCallback(
    async (request) => {
      setState({ type: 'sending' });

      try {
        const { task } = await request();
        setState({ type: 'processing', progress: task.progress });

        const result = await waitTaskCompleted<TResult, TStep>(task, {
          onTick,
          timeoutMs: options.timeoutMs,
        });

        return { type: 'result', result };
      } catch (error) {
        if (error instanceof RunAsyncUntilCompleteTimedOutError) {
          setState({ type: 'timed-out', error });
          return { type: 'timed-out', error };
        }

        setState({ type: 'request-failed', error: error as Error });

        throw error;
      }
    },
    [onTick, options.timeoutMs],
  );

  return { backgroundTaskState: state, runBackgroundTask };
}
